import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SeedDataImportStatus } from '../../graphql/__generated__/graphql';
import {
  createSeedDataApi,
  type SeedDataApi,
  type SeedDataImport,
  type SeedDataUploadReservation,
} from './seedDataApi';
import { isActiveSeedDataImportStatus } from './seedDataPresentation';
import {
  SeedDataUploadAbortedError,
  uploadSeedDataXlsx,
  type SeedDataUploadTask,
  type SeedDataUploadTransportOptions,
} from './seedDataUploadTransport';

export const SEED_DATA_HISTORY_PAGE_SIZE = 25;
export const SEED_DATA_POLL_INTERVAL_MS = 5_000;

const HISTORY_LOAD_ERROR = 'Unable to load seed data import history. Please try again.';
const UPLOAD_ERROR = 'Unable to upload the XLSX file. Cancel the pending upload before trying again.';
const CANCEL_ERROR = 'Unable to cancel the pending upload. Please try again.';
const RETRY_ERROR = 'Unable to retry the import. Please try again.';
const ERROR_REPORT_ERROR = 'Unable to download the error report. Please try again.';

export type SeedDataVisibilityTarget = {
  visibilityState?: DocumentVisibilityState;
  addEventListener: (type: 'visibilitychange', listener: EventListener) => void;
  removeEventListener: (type: 'visibilitychange', listener: EventListener) => void;
};

export type SeedDataScheduler = {
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
};

const defaultVisibilityTarget: SeedDataVisibilityTarget | null =
  typeof document === 'undefined' ? null : document;

const defaultScheduler: SeedDataScheduler = {
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: handle => globalThis.clearTimeout(handle),
};

export type PendingSeedDataUpload = Pick<
  SeedDataUploadReservation['import'],
  'id' | 'originalFilename' | 'expiresAt' | 'householdOutcomes' | 'voterOutcomes'
> & {
  status: SeedDataImportStatus.AwaitingUpload;
  sizeBytes: number;
  loadedBytes: number;
};

export type SeedDataActiveImport =
  | { kind: 'pending-upload'; import: PendingSeedDataUpload }
  | { kind: 'import'; import: SeedDataImport };

type UserOperation = 'upload' | 'cancel' | 'retry' | 'download' | 'refresh' | null;
type ListRequestSource = 'initial' | 'user' | 'background';

export type UseSeedDataImportsOptions = {
  api?: SeedDataApi;
  uploadTransport?: (options: SeedDataUploadTransportOptions) => SeedDataUploadTask;
  visibilityTarget?: SeedDataVisibilityTarget | null;
  scheduler?: SeedDataScheduler;
  navigateTo?: (url: string) => void;
};

export type UseSeedDataImportsResult = {
  items: SeedDataImport[];
  total: number;
  page: number;
  loading: boolean;
  errorMessage: string | null;
  errorFocusToken: number;
  announcement: string;
  userOperation: UserOperation;
  activeImport: SeedDataActiveImport | null;
  hasActiveImport: boolean;
  canStartUpload: boolean;
  setPage: (page: number) => void;
  refresh: () => Promise<void>;
  upload: (file: File) => Promise<void>;
  cancel: (id: string) => Promise<void>;
  retry: (id: string) => Promise<void>;
  downloadErrors: (id: string) => Promise<void>;
};

const isAbort = (error: unknown, signal?: AbortSignal): boolean =>
  signal?.aborted === true || error instanceof SeedDataUploadAbortedError;

const isVisible = (target: SeedDataVisibilityTarget | null): boolean =>
  target !== null && target.visibilityState !== 'hidden';

const navigateToDownload = (url: string): void => {
  window.location.assign(url);
};

export const useSeedDataImports = ({
  api,
  uploadTransport = uploadSeedDataXlsx,
  visibilityTarget = defaultVisibilityTarget,
  scheduler = defaultScheduler,
  navigateTo = navigateToDownload,
}: UseSeedDataImportsOptions = {}): UseSeedDataImportsResult => {
  const defaultApiRef = useRef<SeedDataApi | null>(null);
  if (api === undefined && defaultApiRef.current === null) defaultApiRef.current = createSeedDataApi();
  const resolvedApi = api ?? defaultApiRef.current;
  if (resolvedApi === null) throw new Error('Seed Data API was not initialized.');

  const [items, setItems] = useState<SeedDataImport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(0);
  const [loading, setLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [userError, setUserError] = useState<string | null>(null);
  const [errorFocusToken, setErrorFocusToken] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const [userOperation, setUserOperationState] = useState<UserOperation>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingSeedDataUpload | null>(null);
  const [knownActiveImportIds, setKnownActiveImportIds] = useState<Set<string>>(() => new Set());

  const mountedRef = useRef(true);
  const pageRef = useRef(page);
  const userOperationRef = useRef<UserOperation>(userOperation);
  const listControllerRef = useRef<AbortController | null>(null);
  const listRequestSequenceRef = useRef(0);
  const uploadTaskRef = useRef<{ id: string; task: SeedDataUploadTask } | null>(null);
  const cancellationRequestedIdsRef = useRef(new Set<string>());

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const setUserOperation = useCallback((operation: UserOperation): void => {
    userOperationRef.current = operation;
    setUserOperationState(operation);
  }, []);

  const clearUserFailure = useCallback((): void => {
    setUserError(null);
  }, []);

  const reportUserFailure = useCallback((message: string): void => {
    if (!mountedRef.current) return;
    setUserError(message);
    setErrorFocusToken(token => token + 1);
  }, []);

  const abortListRequest = useCallback((): void => {
    listRequestSequenceRef.current += 1;
    listControllerRef.current?.abort();
    listControllerRef.current = null;
  }, []);

  const requestPage = useCallback(
    async (source: ListRequestSource): Promise<boolean> => {
      abortListRequest();
      const controller = new AbortController();
      const requestSequence = listRequestSequenceRef.current + 1;
      listRequestSequenceRef.current = requestSequence;
      listControllerRef.current = controller;

      if (source !== 'background') {
        setLoading(true);
        setHistoryError(null);
      }

      try {
        const result = await resolvedApi.listImports(
          { limit: SEED_DATA_HISTORY_PAGE_SIZE, offset: pageRef.current * SEED_DATA_HISTORY_PAGE_SIZE },
          { signal: controller.signal },
        );
        if (!mountedRef.current || controller.signal.aborted || requestSequence !== listRequestSequenceRef.current) return false;

        setItems(result.items);
        setTotal(result.total);
        setKnownActiveImportIds(previous => {
          if (pageRef.current === 0) {
            return new Set(result.items.filter(item => isActiveSeedDataImportStatus(item.status)).map(item => item.id));
          }

          const next = new Set(previous);
          for (const item of result.items) {
            if (isActiveSeedDataImportStatus(item.status)) next.add(item.id);
            else next.delete(item.id);
          }
          return next;
        });

        const lastPage = Math.max(0, Math.ceil(result.total / SEED_DATA_HISTORY_PAGE_SIZE) - 1);
        if (pageRef.current > lastPage) setPageState(lastPage);
        return true;
      } catch (error) {
        if (isAbort(error, controller.signal) || !mountedRef.current || requestSequence !== listRequestSequenceRef.current) {
          return false;
        }

        if (source === 'user') reportUserFailure(HISTORY_LOAD_ERROR);
        else if (source === 'initial') setHistoryError(HISTORY_LOAD_ERROR);
        return false;
      } finally {
        const ownsCurrentRequest = listControllerRef.current === controller;
        if (ownsCurrentRequest) listControllerRef.current = null;
        if (mountedRef.current && source !== 'background' && (ownsCurrentRequest || listControllerRef.current === null)) {
          setLoading(false);
        }
      }
    },
    [abortListRequest, reportUserFailure, resolvedApi],
  );

  useEffect(() => {
    void requestPage('initial');

    return abortListRequest;
  }, [abortListRequest, requestPage, page]);

  useEffect(
    () => {
      mountedRef.current = true;

      return () => {
        mountedRef.current = false;
        abortListRequest();
        uploadTaskRef.current?.task.abort();
      };
    },
    [abortListRequest],
  );

  const hasPollingRecommended = items.some(item => item.pollingRecommended);
  useEffect(() => {
    if (!hasPollingRecommended || !isVisible(visibilityTarget) || userOperation !== null) return undefined;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = (): void => {
      if (timer === null) return;
      scheduler.clearTimeout(timer);
      timer = null;
    };

    const schedule = (): void => {
      if (!active || !isVisible(visibilityTarget) || userOperationRef.current !== null || timer !== null) return;
      timer = scheduler.setTimeout(() => {
        timer = null;
        void (async () => {
          if (!active || !isVisible(visibilityTarget) || userOperationRef.current !== null) return;
          await requestPage('background');
          if (active) schedule();
        })();
      }, SEED_DATA_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange: EventListener = () => {
      if (!isVisible(visibilityTarget)) {
        clearTimer();
        abortListRequest();
        return;
      }
      schedule();
    };

    visibilityTarget?.addEventListener('visibilitychange', handleVisibilityChange);
    schedule();

    return () => {
      active = false;
      clearTimer();
      visibilityTarget?.removeEventListener('visibilitychange', handleVisibilityChange);
      abortListRequest();
    };
  }, [abortListRequest, hasPollingRecommended, requestPage, scheduler, userOperation, visibilityTarget]);

  const activeHistoryImport = useMemo(
    () => items.find(item => isActiveSeedDataImportStatus(item.status)) ?? null,
    [items],
  );
  const activeImport: SeedDataActiveImport | null = pendingUpload
    ? { kind: 'pending-upload', import: pendingUpload }
    : activeHistoryImport
      ? { kind: 'import', import: activeHistoryImport }
      : null;
  const hasActiveImport = pendingUpload !== null || knownActiveImportIds.size > 0;

  const refresh = useCallback(async (): Promise<void> => {
    if (userOperationRef.current !== null) return;

    clearUserFailure();
    setUserOperation('refresh');
    try {
      if (await requestPage('user')) setAnnouncement('Seed data import history refreshed.');
    } finally {
      if (mountedRef.current) setUserOperation(null);
    }
  }, [clearUserFailure, requestPage, setUserOperation]);

  const upload = useCallback(
    async (file: File): Promise<void> => {
      if (userOperationRef.current !== null || hasActiveImport) return;

      clearUserFailure();
      setUserOperation('upload');
      let pendingUploadId: string | null = null;
      try {
        const reservation = await resolvedApi.createUpload({ originalFilename: file.name, sizeBytes: file.size });
        if (!mountedRef.current) return;

        const nextPendingUpload: PendingSeedDataUpload = {
          id: reservation.import.id,
          originalFilename: reservation.import.originalFilename,
          status: SeedDataImportStatus.AwaitingUpload,
          expiresAt: reservation.import.expiresAt,
          householdOutcomes: reservation.import.householdOutcomes,
          voterOutcomes: reservation.import.voterOutcomes,
          sizeBytes: file.size,
          loadedBytes: 0,
        };
        pendingUploadId = nextPendingUpload.id;
        setPendingUpload(nextPendingUpload);
        setKnownActiveImportIds(previous => new Set(previous).add(nextPendingUpload.id));

        const task = uploadTransport({
          file,
          uploadUrl: reservation.uploadUrl,
          onProgress: progress => {
            if (!mountedRef.current) return;
            setPendingUpload(current =>
              current?.id === nextPendingUpload.id
                ? { ...current, loadedBytes: Math.min(current.sizeBytes, progress.loadedBytes) }
                : current,
            );
          },
        });
        uploadTaskRef.current = { id: nextPendingUpload.id, task };
        await task.promise;
        if (!mountedRef.current) return;
        if (cancellationRequestedIdsRef.current.has(nextPendingUpload.id)) return;

        uploadTaskRef.current = null;
        await resolvedApi.confirmUpload(nextPendingUpload.id);
        if (!mountedRef.current) return;

        setPendingUpload(null);
        setKnownActiveImportIds(previous => new Set(previous).add(nextPendingUpload.id));
        await requestPage('user');
        if (mountedRef.current) setAnnouncement('Seed data upload confirmed and queued for import.');
      } catch (error) {
        if (!isAbort(error)) reportUserFailure(UPLOAD_ERROR);
      } finally {
        if (pendingUploadId !== null && uploadTaskRef.current?.id === pendingUploadId) uploadTaskRef.current = null;
        if (pendingUploadId !== null) cancellationRequestedIdsRef.current.delete(pendingUploadId);
        if (mountedRef.current && userOperationRef.current !== 'cancel') setUserOperation(null);
      }
    },
    [clearUserFailure, hasActiveImport, reportUserFailure, requestPage, resolvedApi, setUserOperation, uploadTransport],
  );

  const cancel = useCallback(
    async (id: string): Promise<void> => {
      if (userOperationRef.current === 'cancel') return;

      clearUserFailure();
      setUserOperation('cancel');
      cancellationRequestedIdsRef.current.add(id);
      if (uploadTaskRef.current?.id === id) uploadTaskRef.current.task.abort();

      try {
        await resolvedApi.cancelUpload(id);
        if (!mountedRef.current) return;

        setPendingUpload(current => (current?.id === id ? null : current));
        setKnownActiveImportIds(previous => {
          const next = new Set(previous);
          next.delete(id);
          return next;
        });
        await requestPage('user');
        if (mountedRef.current) setAnnouncement('Seed data upload cancelled.');
      } catch {
        reportUserFailure(CANCEL_ERROR);
      } finally {
        if (mountedRef.current) setUserOperation(null);
      }
    },
    [clearUserFailure, reportUserFailure, requestPage, resolvedApi, setUserOperation],
  );

  const retry = useCallback(
    async (id: string): Promise<void> => {
      if (userOperationRef.current !== null) return;

      clearUserFailure();
      setUserOperation('retry');
      try {
        await resolvedApi.retryImport(id);
        if (!mountedRef.current) return;

        setKnownActiveImportIds(previous => new Set(previous).add(id));
        await requestPage('user');
        if (mountedRef.current) setAnnouncement('Seed data import retry queued.');
      } catch {
        reportUserFailure(RETRY_ERROR);
      } finally {
        if (mountedRef.current) setUserOperation(null);
      }
    },
    [clearUserFailure, reportUserFailure, requestPage, resolvedApi, setUserOperation],
  );

  const downloadErrors = useCallback(
    async (id: string): Promise<void> => {
      if (userOperationRef.current !== null) return;

      clearUserFailure();
      setUserOperation('download');
      try {
        const report = await resolvedApi.getErrorReportUrl(id);
        if (report === null || typeof report.url !== 'string' || report.url.trim().length === 0) {
          reportUserFailure(ERROR_REPORT_ERROR);
          return;
        }

        // The short-lived URL is intentionally consumed immediately and never
        // placed in React state, local storage, logs, or rendered markup.
        navigateTo(report.url);
        if (mountedRef.current) setAnnouncement('Opening the error report download.');
      } catch {
        reportUserFailure(ERROR_REPORT_ERROR);
      } finally {
        if (mountedRef.current) setUserOperation(null);
      }
    },
    [clearUserFailure, navigateTo, reportUserFailure, resolvedApi, setUserOperation],
  );

  const setPage = useCallback((nextPage: number): void => {
    if (!Number.isInteger(nextPage) || nextPage < 0) return;
    setPageState(nextPage);
  }, []);

  return {
    items,
    total,
    page,
    loading,
    errorMessage: userError ?? historyError,
    errorFocusToken,
    announcement,
    userOperation,
    activeImport,
    hasActiveImport,
    canStartUpload: !loading && !hasActiveImport && userOperation === null,
    setPage,
    refresh,
    upload,
    cancel,
    retry,
    downloadErrors,
  };
};
